/**
 * Cross-entity aggregator: combines `getKnowledgeBases` (entities/knowledge-base)
 * with `useCurrentWorkspaceId` (entities/workspace) to produce the
 * workspace-scoped KB list. FSD prohibits cross-entity value imports between
 * sibling entity slices, so this aggregator MUST live above the entity layer.
 *
 * Putting it in `widgets/` would block features from consuming it (features →
 * widgets is also banned), so `shared/` is the pragmatic home. The
 * `shared/hooks/useKnowledgeBasesList/` directory is classified under the
 * `shared-cross-entity-aggregator` element type in `eslint.config.js`, which
 * grants it (and ONLY it) `shared → entities` value imports.
 *
 * Mirrors the pattern established by `useWorkspaceAuth`.
 *
 * Lifted from `features/knowledge-bases/model/useKnowledgeBasesList/`
 * (re-exported from old path for backward compat).
 */
import { EnhancedKnowledgeBaseSummaryDto } from '@inference.engine/assets-api-client/web';
import { useQuery } from '@tanstack/react-query';
import { getKnowledgeBases, KNOWLEDGE_BASES_QUERY_KEYS } from '@/entities/knowledge-base';
import { useCurrentWorkspaceId } from '@/entities/workspace';

const getKnowledgeBasesFilteredByWorkspace = async (
  workspaceId?: string,
  includeInternal?: boolean,
): Promise<EnhancedKnowledgeBaseSummaryDto[]> => {
  const allKnowledgeBases = await getKnowledgeBases(undefined, includeInternal);
  // The API already confines a regular knowledge base to the caller's workspace, through its grants.
  // `workspaceId` on it is only the first of several, so comparing it here would hide a knowledge
  // base shared with this workspace from every workspace but that one. A foundation knowledge base
  // is not covered by the grants and can still carry a workspace of its own.
  return allKnowledgeBases.filter(
    (item) => !item.isInternal || !item.workspaceId || item.workspaceId === workspaceId,
  );
};

export const useKnowledgeBasesList = (includeInternal?: boolean, enabled = true) => {
  const currentWorkspaceId = useCurrentWorkspaceId();
  return useQuery({
    queryKey: KNOWLEDGE_BASES_QUERY_KEYS.list(currentWorkspaceId, includeInternal),
    queryFn: () => {
      if (!currentWorkspaceId) {
        throw new Error('Missing required parameters for fetching knowledge bases');
      }
      return getKnowledgeBasesFilteredByWorkspace(currentWorkspaceId, includeInternal);
    },
    enabled: enabled && !!currentWorkspaceId,
  });
};
